import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, ChevronDown, FileText, Briefcase, GraduationCap, Download, Wand2, Upload, Check, Circle, X, ArrowLeft, Plus } from 'lucide-react';
import { apiRequest } from '../utils/api';
import { useAuth } from '../components/auth/AuthComponents';
import { useUsage } from '../contexts/UsageContext';
import { useNavigate } from 'react-router-dom';
import SubscriptionNotification from '../components/shared/SubscriptionNotification';
import ProcessingDisclaimerModal, { useProcessingDisclaimer } from '../components/shared/ProcessingDisclaimerModal';
import { COUNTRY_OPTIONS } from '../constants/countries';
import { LANGUAGE_OPTIONS } from '../constants/languages';
import {
  CONTRACT_TYPE_OPTIONS,
  DEGREE_OPTIONS,
  PROGRAM_TYPE_OPTIONS,
  PURPOSE_OPTIONS,
  TONE_OPTIONS,
} from '../constants/formOptions';
import FormSelect from '../components/shared/FormSelect';
import MultiSelect from '../components/shared/MultiSelect';
import DatePickerField from '../components/shared/DatePickerField';
import DurationRangeField from '../components/shared/DurationRangeField';
import { canGenerateOrRevampContract } from '../utils/userPermissions';

const descriptionToBullets = (text = '') =>
  String(text || '')
    .split(/\n+/)
    .map((line) => line.replace(/^[-•*\d.)\s]+/, '').trim())
    .filter(Boolean);

const bulletsToDescription = (bullets = []) =>
  bullets
    .map((b) => `- ${String(b).replace(/^[-•*\s]+/, '').trim()}`)
    .filter((b) => b.length > 2)
    .join('\n');

const appendBullet = (current, bullet) => {
  const clean = String(bullet || '').replace(/^[-•*\s]+/, '').trim();
  if (!clean) return current || '';
  const existing = descriptionToBullets(current);
  if (existing.some((b) => b.toLowerCase() === clean.toLowerCase())) {
    return bulletsToDescription(existing);
  }
  return bulletsToDescription([...existing, clean]);
};

const skillsToList = (text = '') =>
  String(text || '')
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const listToSkills = (skills = []) => skills.join(', ');

const appendSkill = (current, skill) => {
  const clean = String(skill || '').trim();
  if (!clean) return current || '';
  const existing = skillsToList(current);
  if (existing.some((s) => s.toLowerCase() === clean.toLowerCase())) {
    return listToSkills(existing);
  }
  return listToSkills([...existing, clean]);
};

const normalizeEducationForForm = (edu = {}) => {
  let course = ['course', 'field', 'major', 'field_of_study']
    .map((key) => String(edu[key] || '').trim())
    .find(Boolean) || '';
  let degree = String(edu.degree || '').trim();

  if (!course && degree) {
    const inMatch = degree.match(/^(.+?)\s+in\s+(.+)$/i);
    if (inMatch) {
      degree = inMatch[1].trim();
      course = inMatch[2].trim();
    }
  }

  return {
    institution: edu.institution || '',
    course,
    degree,
    duration: edu.duration || '',
    location: edu.location || '',
  };
};

const serializeEducationForApi = (education = []) =>
  (education || []).map((edu) => ({
    institution: edu.institution,
    course: edu.course,
    degree: edu.degree,
    duration: edu.duration || '2016-2020',
    location: edu.location || 'Nigeria',
  }));

const isBlank = (value) => !String(value ?? '').trim();

function RequiredStar() {
  return <span className="text-red-400 ml-1" aria-hidden="true">*</span>;
}

function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-sm text-red-400" role="alert">{message}</p>;
}

function FormField({ label, required = false, error, hint, labelExtra, children, className = '' }) {
  return (
    <div className={className}>
      {label ? (
        <div className={`flex items-center gap-2 mb-2 ${labelExtra ? 'justify-between' : ''}`}>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)]">
            {label}
            {required ? <RequiredStar /> : null}
          </label>
          {labelExtra}
        </div>
      ) : null}
      {hint ? <p className="text-sm text-[var(--color-text-muted)] mb-2">{hint}</p> : null}
      {children}
      <FieldError message={error} />
    </div>
  );
}

function FieldHint({ children, className = '' }) {
  return <p className={`text-sm text-[var(--color-text-muted)] ${className}`}>{children}</p>;
}

function ReviewSummaryLine({ label, value }) {
  const display = Array.isArray(value)
    ? value.filter(Boolean).join(', ')
    : String(value ?? '').trim();
  return (
    <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
      <strong className="text-[var(--color-text)]">{label}:</strong>{' '}
      {display || 'Not provided'}
    </p>
  );
}

function ReviewSummaryBlock({ title, children }) {
  return (
    <div className="space-y-2">
      {title ? <h4 className="text-sm font-semibold text-[var(--color-primary)] uppercase tracking-wide">{title}</h4> : null}
      {children}
    </div>
  );
}

function AiSuggestButton({
  onClick,
  loading = false,
  loadingLabel = 'Working...',
  label = 'AI suggestion',
  compact = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`brand-gradient-btn inline-flex items-center gap-1.5 rounded-lg font-medium disabled:opacity-60 shrink-0 ${
        compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
      }`}
    >
      <Wand2 className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
      {loading ? loadingLabel : label}
    </button>
  );
}

function fieldInputClass(error) {
  return `themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent ${
    error ? 'border-red-500 focus:ring-red-500' : ''
  }`;
}

function validateWizardStep(documentType, stepId, formData) {
  const errors = {};
  const req = (key, label) => {
    if (isBlank(formData[key])) errors[key] = `${label} is required.`;
  };

  if (documentType === 'cv') {
    if (stepId === 'personal') {
      req('fullName', 'Full name');
      req('email', 'Email address');
      req('phone', 'Phone number');
      req('address', 'Address');
      req('state', 'State');
      req('nationality', 'Nationality');
      if (isBlank(formData.languages)) errors.languages = 'Select at least one language.';
    } else if (stepId === 'career') {
      req('careerObjective', 'Career objective');
      if (isBlank(formData.purpose)) errors.purpose = 'Purpose is required.';
    } else if (stepId === 'experience') {
      const experiences = formData.experiences || [];
      if (experiences.length === 0) {
        errors.experiences = 'Add at least one work experience.';
      }
      experiences.forEach((exp, i) => {
        if (isBlank(exp.company)) errors[`experiences.${i}.company`] = 'Company name is required.';
        if (isBlank(exp.role)) errors[`experiences.${i}.role`] = 'Job role is required.';
        if (isBlank(exp.address)) errors[`experiences.${i}.address`] = 'Location is required.';
        if (isBlank(exp.duration)) errors[`experiences.${i}.duration`] = 'Duration is required.';
        if (descriptionToBullets(exp.description).length === 0) {
          errors[`experiences.${i}.description`] = 'Add role experience bullets.';
        }
      });
    } else if (stepId === 'education') {
      const education = formData.education || [];
      if (education.length === 0) {
        errors.education = 'Add at least one education entry.';
      }
      education.forEach((edu, i) => {
        if (isBlank(edu.institution)) errors[`education.${i}.institution`] = 'Institution is required.';
        if (isBlank(edu.course)) errors[`education.${i}.course`] = 'Course of study is required.';
        if (isBlank(edu.degree)) errors[`education.${i}.degree`] = 'Degree is required.';
        if (isBlank(edu.duration)) errors[`education.${i}.duration`] = 'Duration is required.';
        if (isBlank(edu.location)) errors[`education.${i}.location`] = 'Location is required.';
      });
    }
  } else if (documentType === 'sop') {
    if (stepId === 'program') {
      req('applicantName', 'Full name');
      req('programType', 'Program type');
      req('institution', 'Institution');
      req('course', 'Course');
      req('courseDescription', 'Course description');
    } else if (stepId === 'background') {
      if (isBlank(formData.tone)) errors.tone = 'Tone is required.';
      if (isBlank(formData.yearsExperience)) {
        errors.yearsExperience = 'Years of experience is required.';
      } else if (Number.isNaN(Number(formData.yearsExperience))) {
        errors.yearsExperience = 'Years of experience must be a number.';
      }
    }
  } else if (documentType === 'contract') {
    if (stepId === 'basic') {
      req('contractType', 'Contract type');
      req('contractDate', 'Contract date');
      req('jurisdiction', 'Governing jurisdiction');
    } else if (stepId === 'parties') {
      req('party1Name', 'Party 1 legal name');
      req('party1Address', 'Party 1 address');
      req('party2Name', 'Party 2 legal name');
      req('party2Address', 'Party 2 address');
      req('scope', 'Scope of agreement');
    }
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

function validateDocumentForm(documentType, formData) {
  const steps = {
    cv: ['personal', 'career', 'experience', 'education'],
    sop: ['program', 'background'],
    contract: ['basic', 'parties'],
  }[documentType] || [];

  const allErrors = {};
  steps.forEach((stepId) => {
    const { errors } = validateWizardStep(documentType, stepId, formData);
    Object.assign(allErrors, errors);
  });
  return { valid: Object.keys(allErrors).length === 0, errors: allErrors };
}

function findFirstInvalidStepIndex(documentType, formData, steps) {
  for (let i = 0; i < steps.length; i++) {
    const stepId = steps[i].id;
    if (stepId === 'review' || stepId === 'skills' || stepId === 'terms' || stepId === 'clauses') continue;
    const { valid } = validateWizardStep(documentType, stepId, formData);
    if (!valid) return i;
  }
  return -1;
}

const GenerateDocument = ({ setCurrentPage }) => {
  const { user } = useAuth();
  const { refreshUsage } = useUsage();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [documentType, setDocumentType] = useState('');
  const [formData, setFormData] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState({});
  const [cvFile, setCvFile] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDocumentId, setEditDocumentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expSuggestions, setExpSuggestions] = useState({});
  const [suggestionsOpen, setSuggestionsOpen] = useState({});
  const [collapsedExperiences, setCollapsedExperiences] = useState({});
  const [suggestingExpIndex, setSuggestingExpIndex] = useState(null);
  const [enhancingExpIndex, setEnhancingExpIndex] = useState(null);
  const [skillSuggestions, setSkillSuggestions] = useState([]);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [suggestingSkills, setSuggestingSkills] = useState(false);
  const [suggestingScope, setSuggestingScope] = useState(false);
  const [suggestingTerms, setSuggestingTerms] = useState(false);
  const [suggestingCareer, setSuggestingCareer] = useState(false);
  const {
    open: disclaimerOpen,
    requestProcessing,
    confirmProcessing,
    dismissDisclaimer,
  } = useProcessingDisclaimer();

  const generateActionLabel = documentType
    ? `Generate ${documentType.toUpperCase()}`
    : 'Generate Document';

  // Document type selection
  const allDocumentTypes = [
    { id: 'cv', name: 'CV/Resume', icon: FileText, description: 'Professional curriculum vitae' },
    { id: 'sop', name: 'Statement of Purpose', icon: GraduationCap, description: 'Academic statement for applications' },
    { id: 'contract', name: 'Legal Contract', icon: Briefcase, description: 'Professional legal agreements' }
  ];
  const documentTypes = allDocumentTypes.filter(
    (type) => type.id !== 'contract' || canGenerateOrRevampContract(user?.role),
  );

  // Step configurations for each document type
  const stepConfigs = {
    cv: [
      { id: 'personal', title: 'Personal Information', icon: FileText },
      { id: 'career', title: 'Career Objective', icon: Briefcase },
      { id: 'experience', title: 'Work Experience', icon: Briefcase },
      { id: 'education', title: 'Education', icon: GraduationCap },
      { id: 'skills', title: 'Skills & Certifications', icon: FileText },
      { id: 'review', title: 'Review & Generate', icon: Check }
    ],
    sop: [
      { id: 'program', title: 'Program Details', icon: GraduationCap },
      { id: 'background', title: 'Background & Experience', icon: FileText },
      { id: 'review', title: 'Review & Generate', icon: Check }
    ],
    contract: [
      { id: 'basic', title: 'Contract Basics', icon: Briefcase },
      { id: 'parties', title: 'Parties Information', icon: FileText },
      { id: 'terms', title: 'Terms & Conditions', icon: Briefcase },
      { id: 'clauses', title: 'Special Clauses', icon: FileText },
      { id: 'review', title: 'Review & Generate', icon: Check }
    ]
  };

  const getCurrentSteps = () => stepConfigs[documentType] || [];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess('');
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleArrayInputChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field]?.map((item, i) => i === index ? value : item) || []
    }));
    setFieldErrors((prev) => {
      const prefix = `${field}.${index}.`;
      const hasMatch = Object.keys(prev).some((key) => key.startsWith(prefix) || key === field);
      if (!hasMatch) return prev;
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (key.startsWith(prefix) || key === field) delete next[key];
      });
      return next;
    });
  };

  const addArrayItem = (field, defaultValue = {}) => {
    if (field === 'experiences') {
      const currentLen = formData.experiences?.length || 0;
      setCollapsedExperiences((prev) => {
        const next = { ...prev };
        for (let i = 0; i < currentLen; i++) next[i] = true;
        delete next[currentLen];
        return next;
      });
    }
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), defaultValue]
    }));
  };

  const reindexExpRecord = (prev, index) => {
    const next = {};
    Object.entries(prev).forEach(([key, value]) => {
      const i = Number(key);
      if (i < index) next[i] = value;
      if (i > index) next[i - 1] = value;
    });
    return next;
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field]?.filter((_, i) => i !== index) || []
    }));
    if (field === 'experiences') {
      setExpSuggestions((prev) => reindexExpRecord(prev, index));
      setSuggestionsOpen((prev) => reindexExpRecord(prev, index));
      setCollapsedExperiences((prev) => reindexExpRecord(prev, index));
    }
  };

  const suggestExperienceBullets = async (index) => {
    const exp = formData.experiences?.[index];
    if (!exp?.role?.trim()) {
      setError('Enter the job role first to get matching suggestions.');
      return;
    }
    setSuggestingExpIndex(index);
    setError('');
    try {
      const res = await apiRequest('/generation/ai/generate', {
        method: 'POST',
        body: {
          type: 'job_descriptions',
          context: { role: exp.role, company: exp.company || '' },
        },
      });
      const raw = res.content;
      const list = Array.isArray(raw)
        ? raw.map((item) => String(item).trim()).filter(Boolean)
        : descriptionToBullets(typeof raw === 'string' ? raw : '');
      if (!list.length) throw new Error('No suggestions returned');
      setExpSuggestions((prev) => ({ ...prev, [index]: list }));
      setSuggestionsOpen((prev) => ({ ...prev, [index]: true }));
    } catch (err) {
      setError(err.message || 'Could not generate experience suggestions.');
    } finally {
      setSuggestingExpIndex(null);
    }
  };

  const enhanceExperienceBullets = async (index) => {
    const exp = formData.experiences?.[index];
    if (!exp?.role?.trim()) {
      setError('Enter the job role first.');
      return;
    }
    if (descriptionToBullets(exp.description).length === 0) {
      setError('Add some bullets first, then enhance with AI.');
      return;
    }
    setEnhancingExpIndex(index);
    setError('');
    try {
      const res = await apiRequest('/generation/ai/generate', {
        method: 'POST',
        body: {
          type: 'enhance_experience',
          context: {
            role: exp.role,
            company: exp.company || '',
            draft: exp.description || '',
          },
        },
      });
      const content = typeof res.content === 'string' ? res.content.trim() : '';
      if (!content) throw new Error('Empty enhancement');
      handleArrayInputChange('experiences', index, {
        ...exp,
        description: bulletsToDescription(descriptionToBullets(content)) || content,
      });
    } catch (err) {
      setError(err.message || 'Could not enhance experience bullets.');
    } finally {
      setEnhancingExpIndex(null);
    }
  };

  const suggestSkills = async () => {
    const experiences = formData.experiences || [];
    const jobRoles = experiences.map((e) => e.role?.trim()).filter(Boolean);
    if (jobRoles.length === 0) {
      setError('Add at least one job role in Work Experience first.');
      return;
    }
    setSuggestingSkills(true);
    setError('');
    try {
      const res = await apiRequest('/generation/ai/generate', {
        method: 'POST',
        body: {
          type: 'skills',
          context: {
            job_roles: jobRoles,
            experiences: experiences.map((e) => ({
              role: e.role || '',
              company: e.company || '',
              description: e.description || '',
            })),
          },
        },
      });
      const raw = res.content;
      const list = Array.isArray(raw)
        ? raw.map((item) => String(item).trim()).filter(Boolean)
        : skillsToList(typeof raw === 'string' ? raw : '');
      if (!list.length) throw new Error('No skill suggestions returned');
      setSkillSuggestions(list);
      setSkillsOpen(true);
    } catch (err) {
      setError(err.message || 'Could not generate skill suggestions.');
    } finally {
      setSuggestingSkills(false);
    }
  };

  const addSuggestedSkill = (skill) => {
    handleInputChange('manualSkills', appendSkill(formData.manualSkills, skill));
  };

  const suggestScope = async () => {
    if (!formData.contractType?.trim() || !formData.jurisdiction?.trim()) {
      setError('Select contract type and jurisdiction in Contract Basics first.');
      return;
    }
    setSuggestingScope(true);
    setError('');
    try {
      const res = await apiRequest('/generation/ai/generate', {
        method: 'POST',
        body: {
          type: 'scope_of_agreement',
          context: {
            contract_type: formData.contractType,
            jurisdiction: formData.jurisdiction,
          },
        },
      });
      const content = typeof res.content === 'string' ? res.content.trim() : '';
      if (!content) throw new Error('Empty suggestion');
      handleInputChange('scope', content);
      setSuccess('Scope suggested — you can edit before continuing.');
    } catch (err) {
      setError(err.message || 'Could not generate scope suggestion.');
    } finally {
      setSuggestingScope(false);
    }
  };

  const suggestTerms = async () => {
    if (!formData.contractType?.trim() || !formData.jurisdiction?.trim()) {
      setError('Select contract type and jurisdiction in Contract Basics first.');
      return;
    }
    if (!formData.scope?.trim()) {
      setError('Add or generate Scope of Agreement first.');
      return;
    }
    setSuggestingTerms(true);
    setError('');
    try {
      const res = await apiRequest('/generation/ai/generate', {
        method: 'POST',
        body: {
          type: 'terms_conditions',
          context: {
            contract_type: formData.contractType,
            jurisdiction: formData.jurisdiction,
            scope: formData.scope,
          },
        },
      });
      const content = typeof res.content === 'string' ? res.content.trim() : '';
      if (!content) throw new Error('Empty suggestion');
      handleInputChange('termsConditions', content);
      setSuccess('Terms suggested — you can edit before continuing.');
    } catch (err) {
      setError(err.message || 'Could not generate terms suggestion.');
    } finally {
      setSuggestingTerms(false);
    }
  };

  const finetuneCareerObjective = async () => {
    if (!formData.careerObjective?.trim()) {
      setError('Write a career objective first, then finetune with AI.');
      return;
    }
    setSuggestingCareer(true);
    setError('');
    try {
      const res = await apiRequest('/generation/ai/generate', {
        method: 'POST',
        body: {
          type: 'finetune_career_objective',
          context: {
            draft: formData.careerObjective,
            purpose: formData.purpose || 'work',
            role: formData.experiences?.[0]?.role || '',
          },
        },
      });
      const content = typeof res.content === 'string' ? res.content.trim() : '';
      if (!content) throw new Error('Empty suggestion');
      handleInputChange('careerObjective', content);
      setSuccess('Career objective finetuned — you can edit before continuing.');
    } catch (err) {
      setError(err.message || 'Could not finetune career objective.');
    } finally {
      setSuggestingCareer(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCvFile(file);
    }
  };

  const generateDocument = async () => {
    setIsGenerating(true);
    setError('');
    setSuccess('');

    const { valid, errors } = validateDocumentForm(documentType, formData);
    if (!valid) {
      setFieldErrors(errors);
      setError('Please complete all required fields before generating.');
      const steps = getCurrentSteps();
      const invalidStep = findFirstInvalidStepIndex(documentType, formData, steps);
      if (invalidStep >= 0) setCurrentStep(invalidStep);
      setIsGenerating(false);
      return;
    }

    // If in edit mode, update existing document instead of creating new one
    if (isEditMode && editDocumentId) {
      await updateExistingDocument();
      return;
    }

    try {
      let response;
      const token = localStorage.getItem('docxsai-token');

      if (documentType === 'cv') {
        const cvFormData = {
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          nickname: formData.nickname,
          address: formData.address,
          state: formData.state,
          nationality: formData.nationality,
          languages: formData.languages,
          career_objective: formData.careerObjective,
          purpose: formData.purpose,
          experiences: formData.experiences?.map(exp => ({
            company: exp.company,
            job_title: exp.role,
            location: exp.address,
            duration: exp.duration || '2020-2024',
            achievements: descriptionToBullets(exp.description),
          })) || [],
          education: serializeEducationForApi(formData.education),
          manual_skills: formData.manualSkills,
          certifications: formData.certifications
        };

        response = await apiRequest('/generation/cv', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ cv_form: cvFormData })
        });
        

      }
      else if (documentType === 'sop') {
        // Build query parameters for SOP generation
        const queryParams = new URLSearchParams({
          program_type: formData.programType,
          institution: formData.institution,
          course: formData.course,
          tone: formData.tone,
          course_description: formData.courseDescription || '',
          years_experience: formData.yearsExperience || 0,
          applicant_name: formData.applicantName || ''  // Add applicant name
        });


        const formDataToSend = new FormData();
        if (cvFile) {
          formDataToSend.append('cv_file', cvFile);
        }

        response = await apiRequest(`/generation/sop?${queryParams.toString()}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        });
        
      }
      else if (documentType === 'contract') {
        const contractFormData = {
          contract_type: formData.contractType,
          contract_date: formData.contractDate,
          jurisdiction: formData.jurisdiction,
          party1_name: formData.party1Name,
          party1_address: formData.party1Address,
          party2_name: formData.party2Name,
          party2_address: formData.party2Address,
          whereas_clauses: formData.whereasClauses,
          scope: formData.scope,
          terms_conditions: formData.termsConditions,
          special_clauses: formData.specialClauses,
          execution_hints: formData.executionHints
        };

        response = await apiRequest('/generation/contract', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ contract_form: contractFormData })
        });
        
      }

      // Backend returns DocumentGenerationResponse directly on success
      if (response && response.id) {
        const action = isEditMode ? 'updated' : 'generated';
        setSuccess(`${documentType.toUpperCase()} ${action} successfully!`);
        // Refresh usage data after successful generation
        await refreshUsage();
        // Handle download
        if (response.download_url) {
          window.open(response.download_url, '_blank');
        }
        
        // If in edit mode, redirect back to documents list after a delay
        if (isEditMode) {
          setTimeout(() => {
            navigate('/generated-documents');
          }, 2000);
        }
      } else {
        setError('Failed to generate document - invalid response format');
      }

    } catch (err) {
      console.error('Generation error:', err);
      // Handle different error formats
      let errorMessage = 'An error occurred during generation';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.detail) {
        errorMessage = err.detail;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  const nextStep = () => {
    const steps = getCurrentSteps();
    const stepId = steps[currentStep]?.id;
    if (stepId && stepId !== 'review') {
      const { errors } = validateWizardStep(documentType, stepId, formData);
      if (documentType === 'sop' && stepId === 'background' && !cvFile) {
        errors.cvFile = 'Please upload your CV. It is required for SOP generation.';
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setError('Please fill in all required fields marked with *.');
        if (stepId === 'experience') {
          const toExpand = {};
          Object.keys(errors).forEach((key) => {
            const match = key.match(/^experiences\.(\d+)\./);
            if (match) toExpand[match[1]] = false;
          });
          if (Object.keys(toExpand).length) {
            setCollapsedExperiences((prev) => ({ ...prev, ...toExpand }));
          }
        }
        return;
      }
    }
    setError('');
    setFieldErrors({});
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setError('');
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDocumentTypeSelect = (type) => {
    setDocumentType(type);
    setCurrentStep(0);
    setFormData({});
    setGeneratedContent({});
    setCvFile(null);
    setError('');
    setFieldErrors({});
    setSuccess('');
  };

  // Check for edit mode on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const edit = urlParams.get('edit');
    const id = urlParams.get('id');
    const type = urlParams.get('type');

    if (edit === 'true' && id && type) {
      setIsEditMode(true);
      setEditDocumentId(id);
      setDocumentType(type);
      loadDocumentForEdit(id, type);
    }
  }, []);

  const loadDocumentForEdit = async (documentId, docType) => {
    try {
      setLoading(true);
      const response = await apiRequest(`generation/${documentId}`);
      
      if (response) {
        // Map stored data to form fields based on document type
        if (docType === 'cv' && response.cv_form) {
          const cvData = response.cv_form;
          setFormData({
            fullName: cvData.full_name || '',
            email: cvData.email || '',
            phone: cvData.phone || '',
            nickname: cvData.nickname || '',
            address: cvData.address || '',
            state: cvData.state || '',
            nationality: cvData.nationality || '',
            languages: cvData.languages || '',
            careerObjective: cvData.career_objective || '',
            purpose: cvData.purpose || '',
            experiences: cvData.experiences?.map(exp => ({
              company: exp.company || '',
              role: exp.job_title || '',
              address: exp.location || '',
              duration: exp.duration || '',
              description: bulletsToDescription(
                Array.isArray(exp.achievements)
                  ? exp.achievements
                  : descriptionToBullets(exp.description || '')
              ),
            })) || [],
            education: cvData.education?.map(normalizeEducationForForm) || [],
            manualSkills: cvData.manual_skills || '',
            certifications: cvData.certifications || ''
          });
        } else if (docType === 'sop' && response.sop_form) {
          const sopData = response.sop_form;
          setFormData({
            applicantName: sopData.applicant_name || '',  // Add applicant name
            programType: sopData.program_type || '',
            institution: sopData.institution || '',
            course: sopData.course || '',
            courseDescription: sopData.course_description || '',
            yearsExperience: sopData.years_experience || 0,
            tone: sopData.tone || ''
          });
        } else if (docType === 'contract' && response.contract_form) {
          const contractData = response.contract_form;
          setFormData({
            contractType: contractData.contract_type || '',
            contractDate: contractData.contract_date || '',
            jurisdiction: contractData.jurisdiction || '',
            party1Name: contractData.party1_name || '',
            party1Address: contractData.party1_address || '',
            party2Name: contractData.party2_name || '',
            party2Address: contractData.party2_address || '',
            whereasClauses: contractData.whereas_clauses || '',
            scope: contractData.scope || '',
            termsConditions: contractData.terms_conditions || '',
            specialClauses: contractData.special_clauses || '',
            executionHints: contractData.execution_hints || ''
          });
        }
        setGeneratedContent(response.generated_content || {});
      }
    } catch (err) {
      console.error('Failed to load document for edit:', err);
      setError('Failed to load document data for editing');
    } finally {
      setLoading(false);
    }
  };

  const updateExistingDocument = async () => {
    try {
      let response;
      const token = localStorage.getItem('docxsai-token');

      if (documentType === 'cv') {
        const cvFormData = {
          full_name: formData.full_name || formData.fullName,
          email: formData.email,
          phone: formData.phone,
          nickname: formData.nickname,
          address: formData.address,
          state: formData.state,
          nationality: formData.nationality,
          languages: formData.languages,
          career_objective: formData.career_objective || formData.careerObjective,
          purpose: formData.purpose,
          experiences: (formData.experiences || []).map(exp => ({
            company: exp.company,
            job_title: exp.role || exp.job_title,
            location: exp.address || exp.location,
            duration: exp.duration || '2020-2024',
            achievements: descriptionToBullets(exp.description),
          })),
          education: serializeEducationForApi(formData.education),
          manual_skills: formData.manual_skills || formData.manualSkills,
          certifications: formData.certifications
        };

        response = await apiRequest(`/generation/${editDocumentId}/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(cvFormData)
        });

      } 
      else if (documentType === 'sop') {
        const queryParams = new URLSearchParams({
          program_type: formData.program_type || formData.programType,
          institution: formData.institution,
          course: formData.course,
          tone: formData.tone,
          course_description: formData.course_description || formData.courseDescription || '',
          years_experience: formData.years_experience || formData.yearsExperience || 0,
          applicant_name: formData.applicant_name || formData.applicantName || ''  // Add applicant name
        });
      
        const formDataToSend = new FormData();
        if (cvFile) {
          formDataToSend.append('cv_file', cvFile);
        }
      
        response = await apiRequest(`/generation/${editDocumentId}/update?${queryParams.toString()}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        });
      } 
      else if (documentType === 'contract') {
        const contractFormData = {
          contract_type: formData.contract_type || formData.contractType,
          contract_date: formData.contract_date || formData.contractDate,
          jurisdiction: formData.jurisdiction,
          party1_name: formData.party1_name || formData.party1Name,
          party1_address: formData.party1_address || formData.party1Address,
          party2_name: formData.party2_name || formData.party2Name,
          party2_address: formData.party2_address || formData.party2Address,
          whereas_clauses: formData.whereas_clauses || formData.whereasClauses,
          scope: formData.scope,
          terms_conditions: formData.terms_conditions || formData.termsConditions,
          special_clauses: formData.special_clauses || formData.specialClauses,
          execution_hints: formData.execution_hints || formData.executionHints
        };

        response = await apiRequest(`/generation/${editDocumentId}/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(contractFormData)
        });
      }

      if (response && response.id) {
        setSuccess(`${documentType.toUpperCase()} updated successfully!`);
        // Refresh usage data after successful update
        await refreshUsage();
        if (response.download_url) {
          window.open(response.download_url, '_blank');
        }
        setTimeout(() => {
          navigate('/generated-documents');
        }, 2000);
      } else {
        setError('Failed to update document');
      }

    } catch (err) {
      console.error('Update error:', err);
      let errorMessage = 'An error occurred during update';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.detail) {
        errorMessage = err.detail;
      }
      setError(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!documentType) {
    return (
      <div className="min-h-screen bg-[var(--color-bg)]">
        <div className="w-full">
          <div className="mb-12 px-4">
            <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)] mb-4">
              Document{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
                Generator
              </span>
            </h1>
            <p className="text-lg text-[var(--color-text-secondary)]">Choose the type of document you want to create</p>
            
            {/* Subscription Notification */}
            <div className="mt-6">
              <SubscriptionNotification />
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 px-4">
            {documentTypes.map((type) => (
              <div
                key={type.id}
                onClick={() => handleDocumentTypeSelect(type.id)}
                className="elevated-card rounded-xl p-6 sm:p-8 cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-[var(--color-primary)]"
              >
                <div className="flex flex-col items-center text-center">
                  <type.icon className="w-10 h-10 sm:w-12 sm:h-12 text-[var(--color-primary)] mb-4" />
                  <h3 className="text-lg sm:text-xl font-semibold text-[var(--color-text)] mb-2">{type.name}</h3>
                  <p className="text-[var(--color-text-muted)] text-sm">{type.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const steps = getCurrentSteps();
  const currentStepData = steps[currentStep];

  const renderStepContent = () => {
    const stepId = currentStepData?.id;

    switch (stepId) {
      case 'personal':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Personal Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Full Name" required error={fieldErrors.fullName}>
                <input
                  type="text"
                  placeholder="Full Name"
                  value={formData.fullName || ''}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className={fieldInputClass(fieldErrors.fullName)}
                />
              </FormField>
              <FormField label="Email Address" required error={fieldErrors.email}>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={formData.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={fieldInputClass(fieldErrors.email)}
                />
              </FormField>
              <FormField label="Phone Number" required error={fieldErrors.phone}>
                <input
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={fieldInputClass(fieldErrors.phone)}
                />
              </FormField>
              <FormField label="Nickname">
                <input
                  type="text"
                  placeholder="Nickname"
                  value={formData.nickname || ''}
                  onChange={(e) => handleInputChange('nickname', e.target.value)}
                  className={fieldInputClass(false)}
                />
              </FormField>
              <FormField label="Address" required error={fieldErrors.address}>
                <input
                  type="text"
                  placeholder="Address"
                  value={formData.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className={fieldInputClass(fieldErrors.address)}
                />
              </FormField>
              <FormField label="State" required error={fieldErrors.state}>
                <input
                  type="text"
                  placeholder="State"
                  value={formData.state || ''}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className={fieldInputClass(fieldErrors.state)}
                />
              </FormField>
              <FormField label="Nationality" required error={fieldErrors.nationality}>
                <FormSelect
                  value={formData.nationality || ''}
                  onChange={(v) => handleInputChange('nationality', v)}
                  options={COUNTRY_OPTIONS}
                  placeholder="Select nationality"
                  searchable
                  error={!!fieldErrors.nationality}
                />
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Languages" required error={fieldErrors.languages}>
                  <MultiSelect
                    value={formData.languages || ''}
                    onChange={(v) => handleInputChange('languages', v)}
                    options={LANGUAGE_OPTIONS}
                    placeholder="Select languages"
                    error={!!fieldErrors.languages}
                  />
                </FormField>
              </div>
            </div>
          </div>
        );

      case 'career':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Career Objective</h2>
            <div className="elevated-card p-4 rounded-lg">
              <FormField
                label="Career Objective"
                required
                error={fieldErrors.careerObjective}
                hint="Write a draft, then finetune with AI"
                labelExtra={
                  <AiSuggestButton
                    loading={suggestingCareer}
                    loadingLabel="Finetuning..."
                    label="Finetune with AI"
                    compact
                    onClick={finetuneCareerObjective}
                  />
                }
              >
                <textarea
                  placeholder="Write your career objective..."
                  value={formData.careerObjective || ''}
                  onChange={(e) => handleInputChange('careerObjective', e.target.value)}
                  rows={4}
                  className={fieldInputClass(fieldErrors.careerObjective)}
                />
              </FormField>
            </div>
            <FormField label="Purpose" required error={fieldErrors.purpose}>
              <FormSelect
                value={formData.purpose || ''}
                onChange={(v) => handleInputChange('purpose', v)}
                options={PURPOSE_OPTIONS}
                placeholder="Select purpose"
                error={!!fieldErrors.purpose}
              />
            </FormField>
          </div>
        );

      case 'experience':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[var(--color-text)]">Work Experience</h2>
              <button
                onClick={() => addArrayItem('experiences', { company: '', role: '', address: '', duration: '', description: '' })}
                className="px-4 py-2 bg-green-600 text-[var(--color-text)] rounded-lg hover:bg-green-700"
              >
                Add Experience
              </button>
            </div>
            <FieldError message={fieldErrors.experiences} />
            {(formData.experiences || []).length === 0 ? (
              <FieldHint className="mb-2">Add at least one work experience to continue.</FieldHint>
            ) : null}
            {(formData.experiences || []).map((exp, index) => {
              const isCollapsed = !!collapsedExperiences[index];
              const suggestions = expSuggestions[index] || [];
              const showSuggestions = suggestions.length > 0 && !!suggestionsOpen[index];
              const summary =
                [exp.role, exp.company].filter((part) => part?.trim()).join(' · ') ||
                'Click to expand';
              return (
              <div key={index} className="elevated-card rounded-lg p-6">
                <div className="flex items-center justify-between mb-4 gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      setCollapsedExperiences((prev) => ({
                        ...prev,
                        [index]: !prev[index],
                      }))
                    }
                    className="flex items-center gap-2 min-w-0 text-left flex-1"
                  >
                    {isCollapsed ? (
                      <ChevronRight className="w-5 h-5 text-[var(--color-text-muted)] shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[var(--color-text-muted)] shrink-0" />
                    )}
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[var(--color-text)]">Experience {index + 1}</h3>
                      {isCollapsed ? (
                        <p className="text-sm text-[var(--color-text-muted)] truncate">{summary}</p>
                      ) : null}
                    </div>
                  </button>
                  <button
                    onClick={() => removeArrayItem('experiences', index)}
                    className="text-red-400 hover:text-red-300 shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {!isCollapsed ? (
                <>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField label="Company Name" required error={fieldErrors[`experiences.${index}.company`]}>
                    <input
                      type="text"
                      placeholder="Company Name"
                      value={exp.company || ''}
                      onChange={(e) => handleArrayInputChange('experiences', index, { ...exp, company: e.target.value })}
                      className={fieldInputClass(fieldErrors[`experiences.${index}.company`])}
                    />
                  </FormField>
                  <FormField label="Job Role" required error={fieldErrors[`experiences.${index}.role`]}>
                    <input
                      type="text"
                      placeholder="Job Role"
                      value={exp.role || ''}
                      onChange={(e) => handleArrayInputChange('experiences', index, { ...exp, role: e.target.value })}
                      className={fieldInputClass(fieldErrors[`experiences.${index}.role`])}
                    />
                  </FormField>
                  <FormField label="Location" required error={fieldErrors[`experiences.${index}.address`]}>
                    <input
                      type="text"
                      placeholder="Location"
                      value={exp.address || ''}
                      onChange={(e) => handleArrayInputChange('experiences', index, { ...exp, address: e.target.value })}
                      className={fieldInputClass(fieldErrors[`experiences.${index}.address`])}
                    />
                  </FormField>
                  <FormField label="Duration" required error={fieldErrors[`experiences.${index}.duration`]}>
                    <DurationRangeField
                      value={exp.duration || ''}
                      onChange={(v) => handleArrayInputChange('experiences', index, { ...exp, duration: v })}
                      placeholder="From date - To date / Present"
                      precision="month"
                      allowPresent
                      title="Work experience duration"
                      error={!!fieldErrors[`experiences.${index}.duration`]}
                    />
                  </FormField>
                </div>

                <div className="mt-5 elevated-card rounded-lg p-4 bg-[var(--color-surface-muted)]">
                  <FormField
                    label="Role experience bullets"
                    required
                    error={fieldErrors[`experiences.${index}.description`]}
                  >
                  <p className="text-sm text-[var(--color-text-muted)] mb-3 -mt-1">
                    Choose AI suggestions matching this job role, or write your own. Edit as needed, then enhance with AI.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => suggestExperienceBullets(index)}
                      disabled={suggestingExpIndex === index}
                      className="brand-gradient-btn inline-flex flex-1 items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
                    >
                      <Wand2 className="w-4 h-4" />
                      {suggestingExpIndex === index ? 'Suggesting...' : 'AI suggestions'}
                    </button>
                    <button
                      type="button"
                      onClick={() => enhanceExperienceBullets(index)}
                      disabled={enhancingExpIndex === index}
                      className="inline-flex flex-1 items-center justify-center gap-2 px-3 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 text-[var(--color-text)] text-sm font-medium disabled:opacity-60"
                    >
                      <Wand2 className="w-4 h-4" />
                      {enhancingExpIndex === index ? 'Enhancing...' : 'Enhance with AI'}
                    </button>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-4">
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {suggestions.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          Enter a job role, then tap AI suggestions to see matching experience lines.
                        </p>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              setSuggestionsOpen((prev) => ({
                                ...prev,
                                [index]: !prev[index],
                              }))
                            }
                            className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                          >
                            {showSuggestions ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            Showing {suggestions.length} results for{' '}
                            <span className="text-[var(--color-text-secondary)]">{exp.role}</span>
                          </button>
                          {showSuggestions
                            ? suggestions.map((bullet, bi) => (
                            <div key={`${index}-sug-${bi}`} className="flex gap-3 items-start p-3 rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-border)]">
                              <button
                                type="button"
                                onClick={() =>
                                  handleArrayInputChange('experiences', index, {
                                    ...exp,
                                    description: appendBullet(exp.description, bullet),
                                  })
                                }
                                className="shrink-0 w-7 h-7 rounded-full bg-orange-600 hover:bg-orange-500 text-[var(--color-text)] flex items-center justify-center"
                                aria-label="Add suggestion"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                              <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{bullet}</p>
                            </div>
                          ))
                            : null}
                        </>
                      )}
                    </div>
                    <textarea
                      placeholder={"- Led monthly reconciliations...\n- Prepared financial reports...\n- Collaborated with auditors..."}
                      value={exp.description || ''}
                      onChange={(e) => handleArrayInputChange('experiences', index, { ...exp, description: e.target.value })}
                      rows={10}
                      className={fieldInputClass(fieldErrors[`experiences.${index}.description`])}
                    />
                  </div>
                  </FormField>
                </div>
                </>
                ) : null}
              </div>
              );
            })}
          </div>
        );

      case 'education':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[var(--color-text)]">Education</h2>
              <button
                onClick={() => addArrayItem('education', { institution: '', course: '', degree: '', duration: '', location: '' })}
                className="px-4 py-2 bg-green-600 text-[var(--color-text)] rounded-lg hover:bg-green-700"
              >
                Add Education
              </button>
            </div>
            <FieldError message={fieldErrors.education} />
            {(formData.education || []).length === 0 ? (
              <FieldHint className="mb-2">Add at least one education entry to continue.</FieldHint>
            ) : null}
            {(formData.education || []).map((edu, index) => (
              <div key={index} className="elevated-card rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[var(--color-text)]">Education {index + 1}</h3>
                  <button
                    onClick={() => removeArrayItem('education', index)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField label="School/Institution Name" required error={fieldErrors[`education.${index}.institution`]}>
                    <input
                      type="text"
                      placeholder="School/Institution Name"
                      value={edu.institution || ''}
                      onChange={(e) => handleArrayInputChange('education', index, { ...edu, institution: e.target.value })}
                      className={fieldInputClass(fieldErrors[`education.${index}.institution`])}
                    />
                  </FormField>
                  <FormField label="Course of Study" required error={fieldErrors[`education.${index}.course`]}>
                    <input
                      type="text"
                      placeholder="Course of Study"
                      value={edu.course || ''}
                      onChange={(e) => handleArrayInputChange('education', index, { ...edu, course: e.target.value })}
                      className={fieldInputClass(fieldErrors[`education.${index}.course`])}
                    />
                  </FormField>
                  <FormField label="Degree" required error={fieldErrors[`education.${index}.degree`]}>
                    <FormSelect
                      value={edu.degree || ''}
                      onChange={(v) => handleArrayInputChange('education', index, { ...edu, degree: v })}
                      options={DEGREE_OPTIONS}
                      placeholder="Select degree"
                      error={!!fieldErrors[`education.${index}.degree`]}
                    />
                  </FormField>
                  <FormField label="Duration" required error={fieldErrors[`education.${index}.duration`]}>
                    <DurationRangeField
                      value={edu.duration || ''}
                      onChange={(v) => handleArrayInputChange('education', index, { ...edu, duration: v })}
                      placeholder="Start year - End year"
                      precision="year"
                      title="Education duration"
                      error={!!fieldErrors[`education.${index}.duration`]}
                    />
                  </FormField>
                  <FormField label="Location" required error={fieldErrors[`education.${index}.location`]} className="md:col-span-2">
                    <input
                      type="text"
                      placeholder="Location"
                      value={edu.location || ''}
                      onChange={(e) => handleArrayInputChange('education', index, { ...edu, location: e.target.value })}
                      className={fieldInputClass(fieldErrors[`education.${index}.location`])}
                    />
                  </FormField>
                </div>
              </div>
            ))}
          </div>
        );

      case 'skills':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Skills & Certifications</h2>
            <p className="text-sm text-[var(--color-text-muted)]">Skills and certifications are optional.</p>

            <div className="elevated-card rounded-lg p-4 bg-[var(--color-surface-muted)] space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1">Skills (optional)</label>
                <p className="text-sm text-[var(--color-text-muted)] mb-3">
                  Get AI suggestions based on your work experience, or type your own (comma-separated).
                </p>
                <button
                  type="button"
                  onClick={suggestSkills}
                  disabled={suggestingSkills}
                  className="brand-gradient-btn inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
                >
                  <Wand2 className="w-4 h-4" />
                  {suggestingSkills ? 'Suggesting...' : 'AI suggestions'}
                </button>
              </div>

              {skillSuggestions.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  <button
                    type="button"
                    onClick={() => setSkillsOpen((o) => !o)}
                    className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]"
                  >
                    {skillsOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    Showing {skillSuggestions.length} skill suggestions
                  </button>
                  {skillsOpen
                    ? skillSuggestions.map((skill, si) => (
                        <div
                          key={`skill-sug-${si}`}
                          className="flex gap-3 items-start p-3 rounded-lg bg-[var(--color-surface-muted)] border border-[var(--color-border)]"
                        >
                          <button
                            type="button"
                            onClick={() => addSuggestedSkill(skill)}
                            className="shrink-0 w-7 h-7 rounded-full bg-orange-600 hover:bg-orange-500 text-[var(--color-text)] flex items-center justify-center"
                            aria-label="Add skill"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">{skill}</p>
                        </div>
                      ))
                    : null}
                </div>
              ) : null}

              <input
                type="text"
                placeholder="e.g., JavaScript, Python, Project Management"
                value={formData.manualSkills || ''}
                onChange={(e) => handleInputChange('manualSkills', e.target.value)}
                className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Certifications (optional)</label>
              <textarea
                placeholder="List your certifications..."
                value={formData.certifications || ''}
                onChange={(e) => handleInputChange('certifications', e.target.value)}
                rows={3}
                className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
              />
            </div>
          </div>
        );
      
            
      case 'program':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Program Details</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Your Full Name" required error={fieldErrors.applicantName} className="md:col-span-2">
                <input
                  type="text"
                  placeholder="Your Full Name"
                  value={formData.applicantName || ''}
                  onChange={(e) => handleInputChange('applicantName', e.target.value)}
                  className={fieldInputClass(fieldErrors.applicantName)}
                />
              </FormField>
              <FormField label="Program Type" required error={fieldErrors.programType}>
                <FormSelect
                  value={formData.programType || ''}
                  onChange={(v) => handleInputChange('programType', v)}
                  options={PROGRAM_TYPE_OPTIONS}
                  placeholder="Select program type"
                  error={!!fieldErrors.programType}
                />
              </FormField>
              <FormField label="Institution" required error={fieldErrors.institution}>
                <input
                  type="text"
                  placeholder="Institution"
                  value={formData.institution || ''}
                  onChange={(e) => handleInputChange('institution', e.target.value)}
                  className={fieldInputClass(fieldErrors.institution)}
                />
              </FormField>
              <FormField label="Course" required error={fieldErrors.course} className="md:col-span-2">
                <input
                  type="text"
                  placeholder="Course"
                  value={formData.course || ''}
                  onChange={(e) => handleInputChange('course', e.target.value)}
                  className={fieldInputClass(fieldErrors.course)}
                />
              </FormField>
            </div>
            
            <div className="elevated-card p-4 rounded-lg">
              <FormField label="Course Description" required error={fieldErrors.courseDescription}>
                <textarea
                  placeholder="Course description..."
                  value={formData.courseDescription || ''}
                  onChange={(e) => handleInputChange('courseDescription', e.target.value)}
                  rows={4}
                  className={fieldInputClass(fieldErrors.courseDescription)}
                />
              </FormField>
            </div>
          </div>
        );
      // case 'program':
      //   return (
      //     <div className="space-y-6">
      //       <h2 className="text-2xl font-bold text-[var(--color-text)]">Program Details</h2>
      //       <div className="grid md:grid-cols-2 gap-4">
      //         <input
      //           type="text"
      //           placeholder="Program Type (e.g., Master, PhD)"
      //           value={formData.programType || ''}
      //           onChange={(e) => handleInputChange('programType', e.target.value)}
      //           className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
      //         />
      //         <input
      //           type="text"
      //           placeholder="Institution"
      //           value={formData.institution || ''}
      //           onChange={(e) => handleInputChange('institution', e.target.value)}
      //           className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
      //         />
      //         <input
      //           type="text"
      //           placeholder="Course"
      //           value={formData.course || ''}
      //           onChange={(e) => handleInputChange('course', e.target.value)}
      //           className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent col-span-2"
      //         />
      //       </div>
            
      //       <div className="elevated-card p-4 rounded-lg">
      //         <div className="flex items-center justify-between mb-4">
      //           <h3 className="font-semibold text-[var(--color-primary)]">Course Description</h3>
      //         </div>
      //         <textarea
      //           placeholder="Course description..."
      //           value={formData.courseDescription || ''}
      //           onChange={(e) => handleInputChange('courseDescription', e.target.value)}
      //           rows={4}
      //           className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
      //         />
      //       </div>
      //     </div>
      //   );

      case 'background':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Background & Experience</h2>
            
            <div>
              <FormField label="Upload CV" required error={fieldErrors.cvFile}>
                <div className="border-2 border-dashed border-[var(--color-border)] rounded-lg p-6 text-center hover:border-[var(--color-primary)] transition-colors">
                  <Upload className="w-8 h-8 text-[var(--color-text-muted)] mx-auto mb-2" />
                  <p className="text-[var(--color-text-muted)] mb-2">Click to upload or drag and drop your CV</p>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    id="cv-upload"
                  />
                  <label htmlFor="cv-upload" className="cursor-pointer">
                    <span className="text-[var(--color-primary)] hover:text-[var(--color-primary)]">Choose file</span>
                  </label>
                  {cvFile ? (
                    <p className="text-green-400 mt-2">✓ {cvFile.name}</p>
                  ) : null}
                </div>
              </FormField>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Years of Experience" required error={fieldErrors.yearsExperience}>
                <input
                  type="number"
                  min="0"
                  placeholder="Years of Experience"
                  value={formData.yearsExperience || ''}
                  onChange={(e) => handleInputChange('yearsExperience', e.target.value)}
                  className={fieldInputClass(fieldErrors.yearsExperience)}
                />
              </FormField>
              <FormField label="Tone" required error={fieldErrors.tone}>
                <FormSelect
                  value={formData.tone || ''}
                  onChange={(v) => handleInputChange('tone', v)}
                  options={TONE_OPTIONS}
                  placeholder="Select tone"
                  error={!!fieldErrors.tone}
                />
              </FormField>
            </div>
          </div>
        );

      case 'basic':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Contract Basics</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Contract Type" required error={fieldErrors.contractType}>
                <FormSelect
                  value={formData.contractType || ''}
                  onChange={(v) => handleInputChange('contractType', v)}
                  options={CONTRACT_TYPE_OPTIONS}
                  placeholder="Select contract type"
                  error={!!fieldErrors.contractType}
                />
              </FormField>
              <FormField label="Date of Contract" required error={fieldErrors.contractDate}>
                <DatePickerField
                  value={formData.contractDate || ''}
                  onChange={(v) => handleInputChange('contractDate', v)}
                  placeholder="Select contract date"
                  title="Select contract date"
                  error={!!fieldErrors.contractDate}
                />
              </FormField>
              <FormField label="Governing Jurisdiction" required error={fieldErrors.jurisdiction} className="md:col-span-2">
                <FormSelect
                  value={formData.jurisdiction || ''}
                  onChange={(v) => handleInputChange('jurisdiction', v)}
                  options={COUNTRY_OPTIONS}
                  placeholder="Search and select country"
                  searchable
                  error={!!fieldErrors.jurisdiction}
                />
              </FormField>
            </div>
          </div>
        );

      case 'parties':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Parties Information</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-[var(--color-text)]">Party 1</h3>
                <FieldHint>
                  You / the first party (e.g. employer, client, landlord, or company offering the deal)
                </FieldHint>
                <FormField label="Legal Name" required error={fieldErrors.party1Name}>
                  <input
                    type="text"
                    placeholder="Legal Name"
                    value={formData.party1Name || ''}
                    onChange={(e) => handleInputChange('party1Name', e.target.value)}
                    className={fieldInputClass(fieldErrors.party1Name)}
                  />
                </FormField>
                <FormField label="Address" required error={fieldErrors.party1Address}>
                  <textarea
                    placeholder="Address"
                    value={formData.party1Address || ''}
                    onChange={(e) => handleInputChange('party1Address', e.target.value)}
                    rows={3}
                    className={fieldInputClass(fieldErrors.party1Address)}
                  />
                </FormField>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold text-[var(--color-text)]">Party 2</h3>
                <FieldHint>
                  The other party (e.g. employee, service provider, tenant, or counterparty)
                </FieldHint>
                <FormField label="Legal Name" required error={fieldErrors.party2Name}>
                  <input
                    type="text"
                    placeholder="Legal Name"
                    value={formData.party2Name || ''}
                    onChange={(e) => handleInputChange('party2Name', e.target.value)}
                    className={fieldInputClass(fieldErrors.party2Name)}
                  />
                </FormField>
                <FormField label="Address" required error={fieldErrors.party2Address}>
                  <textarea
                    placeholder="Address"
                    value={formData.party2Address || ''}
                    onChange={(e) => handleInputChange('party2Address', e.target.value)}
                    rows={3}
                    className={fieldInputClass(fieldErrors.party2Address)}
                  />
                </FormField>
              </div>
            </div>

            <FormField
              label="Scope of Agreement"
              required
              error={fieldErrors.scope}
              labelExtra={
                <AiSuggestButton
                  loading={suggestingScope}
                  loadingLabel="Suggesting..."
                  onClick={suggestScope}
                />
              }
            >
              <textarea
                placeholder="Scope of the agreement..."
                value={formData.scope || ''}
                onChange={(e) => handleInputChange('scope', e.target.value)}
                rows={4}
                className={fieldInputClass(fieldErrors.scope)}
              />
            </FormField>
          </div>
        );

      case 'terms':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Terms & Conditions</h2>
            
            <div className="elevated-card p-4 rounded-lg">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="font-semibold text-[var(--color-primary)]">Terms & Conditions</h3>
                <AiSuggestButton
                  loading={suggestingTerms}
                  loadingLabel="Suggesting..."
                  onClick={suggestTerms}
                />
              </div>
              <FieldHint className="mb-3">Based on type, scope & jurisdiction</FieldHint>
              <textarea
                placeholder="Terms and conditions..."
                value={formData.termsConditions || ''}
                onChange={(e) => handleInputChange('termsConditions', e.target.value)}
                rows={8}
                className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'clauses':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Special Clauses</h2>
            
            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Special Clauses</label>
              <textarea
                placeholder="Any special clauses or provisions..."
                value={formData.specialClauses || ''}
                onChange={(e) => handleInputChange('specialClauses', e.target.value)}
                rows={6}
                className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Execution Hints</label>
              <textarea
                placeholder="Hints on signatories, witnesses, etc..."
                value={formData.executionHints || ''}
                onChange={(e) => handleInputChange('executionHints', e.target.value)}
                rows={4}
                className="themed-input w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent"
              />
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[var(--color-text)]">Review & Generate</h2>
            
            {error && (
              <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-900 border border-green-700 text-green-200 px-4 py-3 rounded-lg">
                {success}
              </div>
            )}
            
            <div className="elevated-card p-6 rounded-lg">
              <h3 className="font-semibold text-[var(--color-text)] mb-4">Summary</h3>
              <div className="max-h-[28rem] overflow-y-auto space-y-5 pr-1">
                {documentType === 'cv' && (
                  <>
                    <ReviewSummaryBlock title="Personal Information">
                      <ReviewSummaryLine label="Name" value={formData.fullName} />
                      <ReviewSummaryLine label="Email" value={formData.email} />
                      <ReviewSummaryLine label="Phone" value={formData.phone} />
                      <ReviewSummaryLine label="Nickname" value={formData.nickname} />
                      <ReviewSummaryLine label="Address" value={formData.address} />
                      <ReviewSummaryLine label="State" value={formData.state} />
                      <ReviewSummaryLine label="Nationality" value={formData.nationality} />
                      <ReviewSummaryLine label="Languages" value={formData.languages} />
                    </ReviewSummaryBlock>

                    <ReviewSummaryBlock title="Career">
                      <ReviewSummaryLine label="Purpose" value={formData.purpose} />
                      <ReviewSummaryLine label="Career Objective" value={formData.careerObjective} />
                    </ReviewSummaryBlock>

                    <ReviewSummaryBlock title="Work Experience">
                      {(formData.experiences || []).length === 0 ? (
                        <ReviewSummaryLine label="Experience" value="None added" />
                      ) : (
                        (formData.experiences || []).map((exp, index) => (
                          <div key={`review-exp-${index}`} className="space-y-1">
                            <ReviewSummaryLine
                              label={`Experience ${index + 1}`}
                              value={[exp.role, exp.company, exp.address, exp.duration].filter(Boolean).join(' · ')}
                            />
                            {descriptionToBullets(exp.description).map((bullet, bulletIndex) => (
                              <p key={`review-exp-${index}-bullet-${bulletIndex}`} className="text-sm text-[var(--color-text-muted)] pl-4">
                                • {bullet}
                              </p>
                            ))}
                          </div>
                        ))
                      )}
                    </ReviewSummaryBlock>

                    <ReviewSummaryBlock title="Education">
                      {(formData.education || []).length === 0 ? (
                        <ReviewSummaryLine label="Education" value="None added" />
                      ) : (
                        (formData.education || []).map((edu, index) => (
                          <ReviewSummaryLine
                            key={`review-edu-${index}`}
                            label={`Education ${index + 1}`}
                            value={[edu.degree, edu.course, edu.institution, edu.duration, edu.location].filter(Boolean).join(' · ')}
                          />
                        ))
                      )}
                    </ReviewSummaryBlock>

                    <ReviewSummaryBlock title="Skills & Certifications">
                      <ReviewSummaryLine label="Skills" value={formData.manualSkills} />
                      <ReviewSummaryLine label="Certifications" value={formData.certifications} />
                    </ReviewSummaryBlock>
                  </>
                )}
                {documentType === 'sop' && (
                  <>
                    <ReviewSummaryLine label="Applicant" value={formData.applicantName} />
                    <ReviewSummaryLine label="Program" value={formData.programType} />
                    <ReviewSummaryLine label="Institution" value={formData.institution} />
                    <ReviewSummaryLine label="Course" value={formData.course} />
                    <ReviewSummaryLine label="Course Description" value={formData.courseDescription} />
                    <ReviewSummaryLine label="Years of Experience" value={formData.yearsExperience} />
                    <ReviewSummaryLine label="Tone" value={formData.tone} />
                  </>
                )}
                {documentType === 'contract' && (
                  <>
                    <ReviewSummaryLine label="Contract Type" value={formData.contractType} />
                    <ReviewSummaryLine label="Date" value={formData.contractDate} />
                    <ReviewSummaryLine label="Jurisdiction" value={formData.jurisdiction} />
                    <ReviewSummaryLine label="Party 1" value={formData.party1Name} />
                    <ReviewSummaryLine label="Party 1 Address" value={formData.party1Address} />
                    <ReviewSummaryLine label="Party 2" value={formData.party2Name} />
                    <ReviewSummaryLine label="Party 2 Address" value={formData.party2Address} />
                    <ReviewSummaryLine label="Scope" value={formData.scope} />
                    <ReviewSummaryLine label="Terms & Conditions" value={formData.termsConditions} />
                    <ReviewSummaryLine label="Special Clauses" value={formData.specialClauses} />
                    <ReviewSummaryLine label="Execution Hints" value={formData.executionHints} />
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-center">
              <button
                onClick={() => requestProcessing(generateDocument)}
                disabled={isGenerating}
                className="flex items-center gap-3 px-8 py-4 bg-green-600 text-[var(--color-text)] rounded-lg hover:bg-green-700 disabled:opacity-50 text-lg font-semibold"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    Generating Document...
                  </>
                ) : (
                  <>
                    <Download className="w-6 h-6" />
                    Generate {documentType.toUpperCase()}
                  </>
                )}
              </button>
            </div>
          </div>
        );

      default:
        return <div className="text-[var(--color-text)]">Step not found</div>;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      <ProcessingDisclaimerModal
        open={disclaimerOpen}
        onConfirm={confirmProcessing}
        onClose={dismissDisclaimer}
        actionLabel={generateActionLabel}
      />
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Generate Document – DocxsAi</title>
        <meta name="description" content="Create CVs, SOPs, and Contracts with AI. This workspace is private and not indexed by search engines." />
        <link rel="canonical" href="https://docxsai.com/generate-document" />
      </Helmet>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                setDocumentType('');
                setCurrentStep(0);
                setFormData({});
                setGeneratedContent({});
                setCvFile(null);
                setError('');
                setFieldErrors({});
                setSuccess('');
              }}
              className="p-2 hover:bg-[var(--color-surface-muted)] rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-[var(--color-text-secondary)]" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)]">
                {isEditMode ? 'Edit' : 'Generate'} {documentType.toUpperCase()}
              </h1>
              <p className="text-[var(--color-text-muted)]">
                {isEditMode ? 'Modify and regenerate your document' : `Step ${currentStep + 1} of ${steps.length}`}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4 overflow-x-auto">
            {steps.map((step, index) => (
              <div key={step.id} className="flex flex-col items-center min-w-0 flex-1">
                <div className={`
                  w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-semibold
                  ${index <= currentStep 
                    ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' 
                    : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]'
                  }
                `}>
                  {index < currentStep ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : index + 1}
                </div>
                <span className="text-xs text-[var(--color-text-muted)] mt-1 text-center max-w-16 sm:max-w-20 truncate">
                  {step.title}
                </span>
              </div>
            ))}
          </div>
          <div className="w-full bg-[var(--color-surface-muted)] rounded-full h-2">
            <div 
              className="bg-[var(--color-primary)] h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="elevated-card rounded-xl p-6 sm:p-8 mb-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-primary)]"></div>
                <span className="text-[var(--color-text)]">Loading document data...</span>
              </div>
            </div>
          ) : (
            <>
              {error && currentStepData?.id !== 'review' && (
                <div className="mb-6 bg-red-500/10 border border-red-500/30 text-[var(--color-danger)] px-4 py-3 rounded-lg" role="alert">
                  {error}
                </div>
              )}
              {success && currentStepData?.id !== 'review' && (
                <div className="mb-6 bg-green-500/10 border border-green-500/30 text-[var(--color-success)] px-4 py-3 rounded-lg" role="status">
                  {success}
                </div>
              )}
              {renderStepContent()}
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-4 sm:px-6 py-3 border border-[var(--color-border)] rounded-lg hover:bg-[var(--color-surface-muted)] disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-text-secondary)]"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>
          
          <button
            onClick={nextStep}
            disabled={currentStep === steps.length - 1}
            className="flex items-center gap-2 px-4 sm:px-6 py-3 bg-[var(--color-primary)] text-[var(--color-text)] rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateDocument; 